import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  combineLatest,
  map,
  mergeMap,
  of,
  switchMap,
  withLatestFrom,
  tap
} from 'rxjs';

import { UserService } from '@cpd-app/core-module/services';
import { Profile, User, UserActionEnum } from '@cpd-app/generated';

import { AuthFacade } from '../auth';
import { adminLogInSuccess, setAuthProfile } from '../auth/auth.actions';
import { LoaderFacade } from '../loader';
import { UserFacade } from './user.facade';
import {
  changePassword,
  changePasswordFailure,
  changePasswordSuccess,
  createAccount,
  createAccountFailure,
  createAccountSuccess,
  deleteUser,
  deleteUserFailure,
  deleteUserSuccess,
  getEmailToVerify,
  getEmailToVerifyFailure,
  getEmailToVerifySuccess,
  getUsers,
  getUsersFailure,
  getUsersSuccess,
  inviteUsers,
  inviteUsersFailure,
  inviteUsersSuccess,
  resetPassword,
  resetPasswordFailure,
  resetPasswordSuccess,
  saveUser,
  saveUserFailure,
  saveUserSuccess,
  verifyEmail,
  verifyEmailFailure,
  verifyEmailSuccess
} from './user.actions';
import { Member } from '@cpd-app/core-module/models';
import { AlertService } from '@cpd-app/core-module/services';

@Injectable()
export class UserEffects {
  public getUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getUsers),
      switchMap(() => {
        return this.userService.getUsers().pipe(
          map((result) =>
            getUsersSuccess({
              users: result as Member[]
            })
          ),
          /*
           * ANALYTICS.22 -- the error is LOGGED, not just swallowed.
           *
           * This caught the error, dispatched a failure action nothing handled,
           * and said nothing anywhere. Combined with a reducer that never
           * cleared `loading`, a failed member load produced a spinner that
           * never stopped and not one clue as to why -- which cost a lot of
           * time to chase. An effect that discards the only description of what
           * went wrong is a debugging dead end.
           */
          catchError((error) => {
            console.error('[users] member load failed:', error);
            return of(getUsersFailure({ error }));
          })
        );
      })
    );
  });

  public getEmailToVerify$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getEmailToVerify),
      switchMap(({ token }) => {
        return this.userService.getEmailToVerify(token).pipe(
          map((email) => getEmailToVerifySuccess({ email })),
          catchError((error) => of(getEmailToVerifyFailure({ error })))
        );
      })
    );
  });

  public saveUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(saveUser),
      switchMap(({ input }) => {
        return this.userService.saveUserDetails(input).pipe(
          withLatestFrom(this.authFacade.authProfile$),
          mergeMap(([user, auth]) => {
            const actions: any[] = [
              saveUserSuccess({
                profile: user.profile as Member
              })
            ];

            if (user.profile.userId === auth.userId) {
              actions.push(
                setAuthProfile({
                  firstName: user.profile.firstName,
                  lastName: user.profile.lastName
                })
              );
            }

            return actions;
          }),
          catchError((error) => of(saveUserFailure({ error })))
        );
      })
    );
  });

  public inviteUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(inviteUsers),
      switchMap(({ input }) => {
        return this.userService.inviteUsers(input).pipe(
          map((success) => inviteUsersSuccess({ success })),
          catchError((error) => of(inviteUsersFailure({ error })))
        );
      })
    );
  });

  public deleteUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteUser),
      switchMap(({ userId }) => {
        return this.userService.deleteUser(userId).pipe(
          map((success) => deleteUserSuccess({ success })),
          catchError((error) => of(deleteUserFailure({ error })))
        );
      })
    );
  });

  public verifyEmail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(verifyEmail),
      switchMap(({ input }) => {
        return this.userService.verifyEmail(input).pipe(
          map((success) =>
            verifyEmailSuccess({ success, verifiedEmail: input.email })
          ),
          catchError((error) => of(verifyEmailFailure({ error })))
        );
      })
    );
  });

  public createAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createAccount),
      withLatestFrom(
        this.userFacade.verifiedEmail$,
        this.userFacade.verificationToken$
      ),
      switchMap(([{ input }, email, token]) => {
        this.loaderFacade.showLoaderUntil(createAccountSuccess.type);

        return this.userService.saveUserCreds({ ...input, ...{ email, token } }).pipe(
          switchMap((data) => {
            this.authFacade.setTokenToStorage(data.token);

            const userInput = {
              firstName: input.firstName,
              lastName: input.lastName,
              userId: data.userId,
              status: UserActionEnum.Active
            };
            return combineLatest([
              this.userService.saveUserDetails(userInput),
              of(data.token)
            ]);
          }),
          switchMap(([user, token]) => {
            return [
              createAccountSuccess({ user: user as User }),
              adminLogInSuccess({
                user: {
                  profile: user.profile as Profile,
                  userId: user.profile?.userId,
                  userEmail: user.profile?.userEmail,
                  userName: user.profile?.userName,
                  userRole: user.profile?.userRole,
                  token
                }
              })
            ];
          }),

          catchError((error) => of(createAccountFailure({ error })))
        );
      })
    );
  });

  public resetPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(resetPassword),
      switchMap(({ email }) => {
        return this.userService.resetPassword(email).pipe(
          map((success) => resetPasswordSuccess({ success })),
          catchError((error) => of(resetPasswordFailure({ error })))
        );
      })
    );
  });

  public changePassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(changePassword),
      switchMap(({ token, password }) => {
        return this.userService.changePassword(token, password).pipe(
          map(({ profile }) =>
            changePasswordSuccess({ account: profile as Member })
          ),
          catchError((error) => of(changePasswordFailure({ error })))
        );
      })
    );
  });

  public failure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          createAccountFailure,
          deleteUserFailure,
          inviteUsersFailure,
          resetPasswordFailure,
          saveUserFailure,
          verifyEmailFailure,
          changePasswordFailure
        ),
        tap(({ error }) => {
          this.loaderFacade.hideLoader();
          this.alertService.showAlert({
            message: error.message,
            autoClose: true,
            isError: true
          });
        })
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private userFacade: UserFacade,
    private userService: UserService,
    private loaderFacade: LoaderFacade,
    private authFacade: AuthFacade,
    private alertService: AlertService
  ) {}
}
